import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { ObjectivesService } from '../objectives.service';
import { GridOptions } from 'ag-grid-community';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { AttributeSelectorModel, AttributeType, SelectedCategoryModel, AttributeValueModel } from 'src/shared/models/attribute-selector.models';
import { AttributeSelectorService } from 'src/app/reusables/attribute-selector/attribute-selector.service';
import { CVLocalizationService } from '../../../reusables/services/cv-localization.service';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'cv-objective-groups-assigned',
    templateUrl: './objective-groups-assigned.component.html',
    styleUrls: ['./objective-groups-assigned.component.scss']
})
export class ObjectiveGroupsAssignedComponent implements OnInit, OnChanges {
    @ViewChild('objectiveGroupsAssignedGrid') agGrid: AgGridAngular;
    @Input() filtertext: string = ""; // Text sent from the outside search box
    @Output() filtertextChange: EventEmitter<string> = new EventEmitter<string>();

    controlType: string = "emp";
    gridOptions: GridOptions = null;
    selectedAttributeCategories: AttributeSelectorModel = null;
    noAttributes: boolean = true;

    constructor(
        public objectiveService: ObjectivesService,
        public singleAttributeService: AttributeSelectorService,
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
    ) {}

    async ngOnInit() {
        await this.cvLocalizationService.GetSection("AgGrid");
    }

    ngOnChanges(changes: SimpleChanges) { // Updates the AG Grid filter when the search box, which is in the landing component, changes, including when it's changed from this component
        if (this.gridOptions && changes.hasOwnProperty("filtertext"))
            this.agGrid?.api.onFilterChanged();
    }

    loadGroups() {
        if (this.objectiveService.groupViewLoading)
            return;

        if (this.controlType == "emp" || this.controlType == "rec")
            this.loadGroupList(this.controlType);
        else {
            this.filterAttrSelection();
        }
    }

    async loadGroupList(type) {
        this.objectiveService.groupViewLoading = true;
        this.gridOptions = null;

        this.objectiveService.loadedGroups = [];
        await this.objectiveService.LoadGroupListRows(type);

        this.setupGridOptions("group");
    }

    setupGridOptions(type: string) {
        this.gridOptions = this.agGridHelperService.createGridOptions({
            columnDefs: [
                {
                    field: type == "group" ? "groupDTO.GroupName" : "Name",
                    colId: type == "group" ? "GroupName" : "ID",
                    //headerName: this.localization.get("Name"),
                    headerName: "Name",
                    onCellClicked: event => {
                        if (event.data.groupDTO == null && event.data.ID != null)
                            this.selectAttributeValue(event.data);
                        else
                            this.selectGroup(event.data);
                    },
                    cellClass: "color-cerulean clickable",
                },
                {
                    field: "inheritCount",
                    colId: "inheritCount",
                    //headerName: this.localization.get("Inherited_Objectives"),
                    headerName: "Inherited Objectives",
                },
                {
                    field: "uniqueCount",
                    colId: "uniqueCount",
                    //headerName: this.localization.get("Unique_Objectives"),
                    headerName: "Unique Objectives",
                }
            ],
            defaultColDef: {
                sortable: true,
                menuTabs: []
            },
            onGridReady: params => {
                if (this.objectiveService.loadedGroups)
                    params.api.setGridOption('rowData', this.objectiveService.loadedGroups);

                params.api.sizeColumnsToFit();
            },
            getRowId: ({ data: rowData }) => rowData.groupDTO != null ? (rowData.groupDTO.GroupID + '-' + rowData.groupDTO.GroupLevel) : rowData.ID,
            isExternalFilterPresent: () => this.filtertext.length > 0,
            doesExternalFilterPass: node => this.agGridHelperService.doesExternalFilterPass(node, this.filtertext),
            onFilterChanged: () => {
                if (this.agGrid.api?.getDisplayedRowCount() === 0) {
                    this.agGrid.api.showNoRowsOverlay();
                }
                else
                    this.agGrid.api?.hideOverlay();
            },
            onGridSizeChanged: gridEvent => {
                if (gridEvent.clientWidth > 0)
                    gridEvent.api.sizeColumnsToFit();
            },
            onColumnVisible: event => event.api.sizeColumnsToFit(),
            overlayNoRowsTemplate:
                `<div style="padding-top: 48px;">
                    <p>
                       ${this.cvLocalizationService.getCachedText("AgGrid", "noRowsToShow", "No Rows To Show")}
                    </p>
                </div>`,
            suppressRowClickSelection: true,
            suppressCellFocus: true,
            suppressMovableColumns: true
        });
    }

    async filterAttrSelection() {
        if (this.selectedAttributeCategories.SelectedCategories.length == 0 || this.objectiveService.groupViewLoading)
            return; // No categories were selected so no values were entered into the dictionary

        this.objectiveService.groupViewLoading = true;
        this.gridOptions = null;
        this.objectiveService.loadedGroups = [];

        //create the objects for the table, load the names, push them into the list (skip all and not set)

        //for the list of selcted ids load
        let tempList: AttributeValueModel[] = [];
        for (var i = 0; i < this.selectedAttributeCategories.SelectedCategories.length; i++) {
            let values: AttributeValueModel[] = await this.singleAttributeService.loadValues(this.selectedAttributeCategories.SelectedCategories[i].SelectedCategoryID, 1);

            for (var j = 0; j < this.selectedAttributeCategories.SelectedCategories[i].SelectedAttributeIDs.length; j++) {
                if (this.selectedAttributeCategories.SelectedCategories[i].SelectedAttributeIDs[j] != 0 && this.selectedAttributeCategories.SelectedCategories[i].SelectedAttributeIDs[j] != -1) {    //skip 0 and -1 as these are "All" and "Not Set" respectively
                    for (var k = 0; k < values.length; k++) {
                        if (values[k].ID == this.selectedAttributeCategories.SelectedCategories[i].SelectedAttributeIDs[j]) {
                            let any: any = values[k];
                            any.inheritCount = 0;
                            any.uniqueCount = 0;

                            for (let loadedObjective of this.objectiveService.loadedObjectives) {
                                // Check for this assignment
                                for (let attrAssignment of loadedObjective.assignments.attr) {
                                    if (loadedObjective.combinedObj.GroupingID == 0 && attrAssignment.AttributeValueID == any.ID) { // Found match
                                        any.uniqueCount++;
                                        break;
                                    }
                                }
                            }

                            tempList = tempList.concat(any);
                            break;
                        }
                    }
                }
            }
        }

        this.objectiveService.groupViewLoading = false;

        this.objectiveService.loadedGroups = tempList;

        this.setupGridOptions("attr");
    }

    onSelectedAttributesChanged(event: AttributeSelectorModel) {
        this.selectedAttributeCategories = event;
    }

    clearSearch() {
        this.filtertext = "";
        this.filtertextChange.emit(this.filtertext);
    }

    selectGroup(obj) {
        this.objectiveService.selectedGroupItem = angular.copy(obj);
        this.objectiveService.selectedAttributeItem = null;
        let type = this.objectiveService.selectedGroupItem.groupDTO.BaseGroupType == 0 ? 'emp' : 'rec';

        //ctrl.setVisibleLevels();

        // Find objective matches
        // If normalized then check for inheritance, Quota metrics do no inherit
        this.objectiveService.selectedGroupCards.quota = [];
        this.objectiveService.selectedGroupCards.normalized = [];
        for (let loadedObjective of this.objectiveService.loadedObjectives) {
            if (this.objectiveService.metricAbrDict[loadedObjective.MetricID].MetricObjectiveType == 0) { // MetricObjectiveType = 0 is Quota
                // Check for this assignment
                if (type == 'emp') {
                    for (let empAssignment of loadedObjective.assignments.emp) {
                        let groupId;
                        switch (empAssignment.GroupLevel) {
                            case 1:
                                groupId = this.objectiveService.selectedGroupItem.groupDTO.Group1ID;
                                break;
                            case 2:
                                groupId = this.objectiveService.selectedGroupItem.groupDTO.Group2ID;
                                break;
                            case 3:
                                groupId = this.objectiveService.selectedGroupItem.groupDTO.Group3ID;
                                break;
                            case 4:
                                groupId = this.objectiveService.selectedGroupItem.groupDTO.Group4ID;
                                break;
                            case 5:
                                groupId = this.objectiveService.selectedGroupItem.groupDTO.Group5ID;
                                break;
                        }

                        if (empAssignment.GroupID == groupId
                            && ((loadedObjective.assignments.coreAssignAtLevel == this.objectiveService.selectedGroupItem.groupDTO.GroupLevel && loadedObjective.assignments.coreAssignAtType == "emp")
                                || (empAssignment.GroupLevel == this.objectiveService.selectedGroupItem.groupDTO.GroupLevel && loadedObjective.assignments.coreAssignAtType == "rec")))
                            this.compileObjectives(loadedObjective);
                    }
                }
                else if (type == 'rec') {
                    for (let recAssignment of loadedObjective.assignments.rec) {
                        let groupId;
                        switch (recAssignment.GroupLevel) {
                            case 1:
                                groupId = this.objectiveService.selectedGroupItem.groupDTO.Group1ID;
                                break;
                            case 2:
                                groupId = this.objectiveService.selectedGroupItem.groupDTO.Group2ID;
                                break;
                            case 3:
                                groupId = this.objectiveService.selectedGroupItem.groupDTO.Group3ID;
                                break;
                            case 4:
                                groupId = this.objectiveService.selectedGroupItem.groupDTO.Group4ID;
                                break;
                            case 5:
                                groupId = this.objectiveService.selectedGroupItem.groupDTO.Group5ID;
                                break;
                        }

                        if (recAssignment.GroupID == groupId
                            && ((loadedObjective.assignments.coreAssignAtLevel == this.objectiveService.selectedGroupItem.groupDTO.GroupLevel && loadedObjective.assignments.coreAssignAtType == "rec")
                                || (recAssignment.GroupLevel == this.objectiveService.selectedGroupItem.groupDTO.GroupLevel && loadedObjective.assignments.coreAssignAtType == "emp")))
                            this.compileObjectives(loadedObjective);
                    }
                }
            }
            else if (this.objectiveService.metricAbrDict[loadedObjective.MetricID].MetricObjectiveType == 1) { // MetricObjectiveType = 1 is Normalized
                // Check for this assignment and inheritance
                if (type == 'emp') {
                    if (loadedObjective.assignments.emp.length == 0) // All emps so match
                        this.objectiveService.selectedGroupCards.normalized.push(angular.copy(loadedObjective));
                    else {
                        for (let empAssignment of loadedObjective.assignments.emp) {
                            if ((empAssignment.GroupLevel == this.objectiveService.selectedGroupItem.groupDTO.GroupLevel && empAssignment.GroupID == this.objectiveService.selectedGroupItem.groupDTO.GroupID)
                                || (empAssignment.GroupLevel > this.objectiveService.selectedGroupItem.groupDTO.GroupLevel && empAssignment.GroupLevel == 5 ? empAssignment.GroupID == this.objectiveService.selectedGroupItem.groupDTO.Group5ID
                                    : empAssignment.GroupLevel == 4 ? empAssignment.GroupID == this.objectiveService.selectedGroupItem.groupDTO.Group4ID
                                        : empAssignment.GroupLevel == 3 ? empAssignment.GroupID == this.objectiveService.selectedGroupItem.groupDTO.Group3ID
                                            : empAssignment.GroupID == this.objectiveService.selectedGroupItem.groupDTO.Group2ID)) { // Found match
                                this.objectiveService.selectedGroupCards.normalized.push(angular.copy(loadedObjective));
                                break;
                            }
                        }
                    }
                }
                else if (type == 'rec') {
                    if (loadedObjective.assignments.rec.length == 0) // All recs so match
                        this.objectiveService.selectedGroupCards.normalized.push(angular.copy(loadedObjective));
                    else {
                        for (let recAssignment of loadedObjective.assignments.rec) {
                            if ((recAssignment.GroupLevel == this.objectiveService.selectedGroupItem.groupDTO.GroupLevel && recAssignment.GroupID == this.objectiveService.selectedGroupItem.groupDTO.GroupID)
                                || (recAssignment.GroupLevel > this.objectiveService.selectedGroupItem.groupDTO.GroupLevel && recAssignment.GroupLevel == 5 ? recAssignment.GroupID == this.objectiveService.selectedGroupItem.groupDTO.Group5ID
                                    : recAssignment.GroupLevel == 4 ? recAssignment.GroupID == this.objectiveService.selectedGroupItem.groupDTO.Group4ID
                                        : recAssignment.GroupLevel == 3 ? recAssignment.GroupID == this.objectiveService.selectedGroupItem.groupDTO.Group3ID
                                            : recAssignment.GroupID == this.objectiveService.selectedGroupItem.groupDTO.Group2ID)) { // Found match
                                this.objectiveService.selectedGroupCards.normalized.push(angular.copy(loadedObjective));
                                break;
                            }
                        }
                    }
                }
            }
        }

        this.objectiveService.showLandingHeader = false;
        this.objectiveService.showGroupsCardView = true;
    }

    private compileObjectives(loadedObjective) {
        let coreCard;
        if (loadedObjective.combinedObj.GroupingID == 0)
            coreCard = loadedObjective;
        else {
            for (let loadedObjectiveSub of this.objectiveService.loadedObjectives) {
                if (loadedObjective.combinedObj.GroupingID == loadedObjectiveSub.combinedObj.ObjectiveID) {
                    coreCard = loadedObjectiveSub;
                    break;
                }
            }
        }

        for (let loadedObjectiveSub of this.objectiveService.loadedObjectives) {
            if (loadedObjectiveSub.combinedObj.ObjectiveID == coreCard.combinedObj.ObjectiveID
                || (loadedObjectiveSub.combinedObj.GroupingID > 0 && loadedObjectiveSub.combinedObj.GroupingID == coreCard.combinedObj.ObjectiveID))
                this.objectiveService.selectedGroupCards.quota.push(angular.copy(loadedObjectiveSub));
        }
    }

    selectAttributeValue(obj) {
        this.objectiveService.selectedGroupItem = null;
        this.objectiveService.selectedAttributeItem = angular.copy(obj);

        // Find objective matches
        // No inheritance on attributes
        this.objectiveService.selectedGroupCards.quota = [];
        this.objectiveService.selectedGroupCards.normalized = [];
        for (let loadedObjective of this.objectiveService.loadedObjectives) {
            if (this.objectiveService.metricAbrDict[loadedObjective.MetricID].MetricObjectiveType == 0) { // MetricObjectiveType = 0 is Quota
                // Check for this assignment
                for (let attrAssignment of loadedObjective.assignments.attr) {
                    if (attrAssignment.AttributeValueID == this.objectiveService.selectedAttributeItem.ID) { // Found match
                        this.objectiveService.selectedGroupCards.quota.push(angular.copy(loadedObjective));
                        break;
                    }
                }
            }
            else if (this.objectiveService.metricAbrDict[loadedObjective.MetricID].MetricObjectiveType == 1) { // MetricObjectiveType = 1 is Normalized
                // Check for this assignment
                for (let attrAssignment of loadedObjective.assignments.attr) {
                    if (attrAssignment.AttributeValueID == this.objectiveService.selectedAttributeItem.ID) { // Found match
                        this.objectiveService.selectedGroupCards.normalized.push(angular.copy(loadedObjective));
                        break;
                    }
                }
            }
        }

        this.objectiveService.showLandingHeader = false;
        this.objectiveService.showGroupsCardView = true;
    }
}
